import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      services: 'Services',
      gallery: 'Gallery',
      about: 'About',
      testimonials: 'Testimonials',
      contact: 'Contact',
    },
    // Hero
    hero: {
      subtitle: 'Professional Home Organization',
      title: 'Transform Your Space,',
      titleAccent: 'Transform Your Life',
      description: 'Galene Home brings serenity and calm to your living spaces. As certified professional organizers, we create sustainable systems that enhance your daily life.',
      cta: 'Explore Services',
      ctaSecondary: 'Get in Touch',
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Thoughtfully designed organization solutions',
      closet: {
        title: 'Seasonal Closet Organization',
        description: 'Transform your wardrobe with our seasonal rotation system. We organize your closet for winter and summer transitions, making your daily routine effortless and serene.',
      },
      decluttering: {
        title: 'Decluttering',
        description: 'Release what no longer serves you. Our gentle decluttering process helps you create space for what truly matters, bringing peace and clarity to your home.',
      },
      newborn: {
        title: 'Newborn Home Preparation',
        description: 'Welcome your new arrival with a perfectly organized home. We prepare your space for baby with thoughtful systems that support this beautiful new chapter.',
      },
      rooms: {
        title: 'Room-by-Room Organization',
        description: 'Every room deserves harmony. From kitchens to home offices, we create custom organization systems tailored to how you live.',
      },
      unpacking: {
        title: 'Moving & Unpacking',
        description: 'Make your new house feel like home from day one. We help you unpack and organize everything from scratch, turning moving chaos into instant serenity.',
      },
      workshops: {
        title: 'Vision Board Workshops',
        description: 'Discover the power of visualization in our creative Vision Board workshops. Design your dreams, set intentions, and create a visual roadmap for the life you want to live.',
      },
      learnMore: 'Learn More',
      requestQuote: 'Request a Quote',
    },
    // Gallery
    gallery: {
      title: 'Transformations',
      subtitle: 'See the before and after of our work',
      before: 'Before',
      after: 'After',
    },
    // About
    about: {
      title: 'About Galene Home',
      subtitle: 'The Story Behind the Serenity',
      story: 'Galene Home was born from a deep belief that our living spaces profoundly impact our daily wellbeing. Named after the Greek goddess of calm seas, we bring tranquility and order to homes, transforming chaotic spaces into serene sanctuaries.',
      mission: 'Our mission is to enhance your daily life through thoughtful organization. We don\'t just organize — we create sustainable systems that grow with you, bringing lasting peace to your home.',
      certifications: 'Certifications',
      certHome: 'Certified Professional Organizer - The Home Academy (NAPO Member School)',
      certNapo: 'The Home Academy is an accredited member of NAPO (National Association of Productivity & Organizing Professionals)',
      philosophy: 'Our Philosophy',
      philosophyText: 'We believe that a calm home leads to a calm mind. Every organizing project we undertake is guided by principles of serenity, functionality, and sustainability.',
    },
    // Testimonials
    testimonials: {
      title: 'What Our Clients Say',
      subtitle: 'Real stories from transformed homes',
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      subtitle: 'Let\'s create your serene space together',
      name: 'Your Name',
      email: 'Email Address',
      service: 'Service of Interest',
      selectService: 'Select a service',
      message: 'Your Message',
      messagePlaceholder: 'Tell us about your space and what you\'d like to achieve...',
      submit: 'Send Message',
      sending: 'Sending...',
      success: 'Message sent successfully! We\'ll get back to you soon.',
      error: 'Something went wrong. Please try again.',
      serviceOptions: {
        closet: 'Seasonal Closet Organization',
        decluttering: 'Decluttering',
        newborn: 'Newborn Home Preparation',
        rooms: 'Room-by-Room Organization',
        unpacking: 'Moving & Unpacking',
        workshops: 'Vision Board Workshops',
        consultation: 'General Consultation',
      },
    },
    // Footer
    footer: {
      tagline: 'Bringing serenity to your home',
      certifications: 'Certified by The Home Academy',
      rights: 'All rights reserved',
      followUs: 'Follow Us',
    },
  },
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      gallery: 'Galería',
      about: 'Sobre Nosotros',
      testimonials: 'Testimonios',
      contact: 'Contacto',
    },
    // Hero
    hero: {
      subtitle: 'Organización Profesional del Hogar',
      title: 'Transforma Tu Espacio,',
      titleAccent: 'Transforma Tu Vida',
      description: 'Galene Home trae serenidad y calma a tus espacios. Como organizadores profesionales certificados, creamos sistemas sostenibles que mejoran tu vida diaria.',
      cta: 'Explorar Servicios',
      ctaSecondary: 'Contáctanos',
    },
    // Services
    services: {
      title: 'Nuestros Servicios',
      subtitle: 'Soluciones de organización diseñadas con cuidado',
      closet: {
        title: 'Organización de Armario Estacional',
        description: 'Transforma tu guardarropa con nuestro sistema de rotación estacional. Organizamos tu armario para las transiciones de invierno y verano, haciendo tu rutina diaria sin esfuerzo y serena.',
      },
      decluttering: {
        title: 'Decluttering',
        description: 'Libera lo que ya no te sirve. Nuestro proceso de decluttering te ayuda a crear espacio para lo que realmente importa, trayendo paz y claridad a tu hogar.',
      },
      newborn: {
        title: 'Preparación del Hogar para Recién Nacidos',
        description: 'Da la bienvenida a tu nuevo bebé con un hogar perfectamente organizado. Preparamos tu espacio con sistemas pensados que apoyan este hermoso nuevo capítulo.',
      },
      rooms: {
        title: 'Organización Habitación por Habitación',
        description: 'Cada habitación merece armonía. Desde cocinas hasta oficinas en casa, creamos sistemas de organización personalizados adaptados a tu forma de vivir.',
      },
      unpacking: {
        title: 'Mudanza y Desembalaje',
        description: 'Haz que tu nueva casa se sienta como hogar desde el primer día. Te ayudamos a desembalar y organizar todo desde cero, convirtiendo el caos de la mudanza en serenidad instantánea.',
      },
      workshops: {
        title: 'Talleres Vision Board',
        description: 'Descubre el poder de la visualización en nuestros talleres creativos de Vision Board. Diseña tus sueños, establece intenciones y crea un mapa visual para la vida que deseas vivir.',
      },
      learnMore: 'Saber Más',
      requestQuote: 'Solicitar Presupuesto',
    },
    // Gallery
    gallery: {
      title: 'Transformaciones',
      subtitle: 'Mira el antes y después de nuestro trabajo',
      before: 'Antes',
      after: 'Después',
    },
    // About
    about: {
      title: 'Sobre Galene Home',
      subtitle: 'La Historia Detrás de la Serenidad',
      story: 'Galene Home nació de la profunda creencia de que nuestros espacios impactan profundamente en nuestro bienestar diario. Nombrado en honor a la diosa griega de los mares en calma, traemos tranquilidad y orden a los hogares, transformando espacios caóticos en santuarios serenos.',
      mission: 'Nuestra misión es mejorar tu vida diaria a través de una organización reflexiva. No solo organizamos — creamos sistemas sostenibles que crecen contigo, trayendo paz duradera a tu hogar.',
      certifications: 'Certificaciones',
      certHome: 'Organizador Profesional Certificado - The Home Academy (Escuela Miembro de NAPO)',
      certNapo: 'The Home Academy es miembro acreditado de NAPO (Asociación Nacional de Profesionales de Productividad y Organización)',
      philosophy: 'Nuestra Filosofía',
      philosophyText: 'Creemos que un hogar tranquilo lleva a una mente tranquila. Cada proyecto de organización que emprendemos está guiado por principios de serenidad, funcionalidad y sostenibilidad.',
    },
    // Testimonials
    testimonials: {
      title: 'Lo Que Dicen Nuestros Clientes',
      subtitle: 'Historias reales de hogares transformados',
    },
    // Contact
    contact: {
      title: 'Contacta con Nosotros',
      subtitle: 'Creemos juntos tu espacio sereno',
      name: 'Tu Nombre',
      email: 'Correo Electrónico',
      service: 'Servicio de Interés',
      selectService: 'Selecciona un servicio',
      message: 'Tu Mensaje',
      messagePlaceholder: 'Cuéntanos sobre tu espacio y lo que te gustaría lograr...',
      submit: 'Enviar Mensaje',
      sending: 'Enviando...',
      success: '¡Mensaje enviado con éxito! Te responderemos pronto.',
      error: 'Algo salió mal. Por favor, inténtalo de nuevo.',
      serviceOptions: {
        closet: 'Organización de Armario Estacional',
        decluttering: 'Decluttering',
        newborn: 'Preparación del Hogar para Recién Nacidos',
        rooms: 'Organización Habitación por Habitación',
        unpacking: 'Mudanza y Desembalaje',
        workshops: 'Talleres Vision Board',
        consultation: 'Consulta General',
      },
    },
    // Footer
    footer: {
      tagline: 'Trayendo serenidad a tu hogar',
      certifications: 'Certificado por The Home Academy',
      rights: 'Todos los derechos reservados',
      followUs: 'Síguenos',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('galene-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('galene-language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
